import React, { useContext, useState, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { Text, Button } from "react_reusable_components";
import { AudioRecorder, useAudioRecorder } from "react-audio-voice-recorder";
import {
  CaretRightOutlined,
  PauseOutlined,
  SaveOutlined,
} from "@ant-design/icons";

import CreatePortalCard from "../../components/CreatePortalCard";
import EvidenceContainer from "../EvidenceContainer/EvidenceContainer";
import CardContainer from "../CardContainer";
import { TwoColumn, TwoRow } from "../../core/layouts";
import { GlobalLoaderContext } from "../../globalContext/globalLoader/globalLoaderProvider";
import {
  getConditionalContent,
  getLocalizedMessage,
} from "../../UtilityFunctions/utils";
import { showNotification } from "../../UtilityFunctions/notification";
import MediaService from "../../services/media-service";
import ConfirmModalContainer from "../ConfirmModalContainer/ConfirmModalContainer";
import variables from "../../themes/base/styles/variables";
import "./style.css";
import moment from "moment";
import { INVALID_DATE } from "../../constants";
import AuditService from "../../services/audit-service";
import { RemoteAuditContext } from "../../globalContext/remoteAudit/remoteAuditProvider";
import { useParams } from "react-router-dom";
import {
  saveInprogressEvidenceInstance,
  setIsEvidenceCompleted,
} from "../../globalContext/remoteAudit/remoteAuditActions";

const AudioContainerStyle = {
  playPauseIcon: {
    color: variables.bodyBg,
    fontSize: variables.iconHeightLarge,
    cursor: "pointer",
  },
  audioContainerStyle: {
    display: "flex",
    flexDirection: "column",
    position: "fixed",
    bottom: variables.marginMediumSmall,
    left: variables.marginMediumSmall,
    backgroundColor: variables.textDefault,
    padding: variables.paddingMedium,
    gap: variables.gapSmaller,
    width: variables.audioRecorderWidth,
    zIndex: 9999999,
  },
  textStyle: {
    fontSize: variables.fontSizeLarge,
    lineHeight: variables.lineHeightXlarge,
    color: variables.bodyBg,
  },
  columnMainStyle: {
    alignItems: "center",
    justifyContent: "center",
    overflow: "unset",
  },
  iconsWrapper: {
    display: "flex",
    alignItems: "center",
    width: variables.fullWidth,
    justifyContent: "center",
    gap: variables.gapSmall,
  },
  btnStyle: {
    backgroundColor: variables.textDefault,
    border: `1px solid ${variables.bodyBg}`,
    display: "flex",
    alignItems: "center",
    height: "unset",
    fontSize: variables.fontSizeNormal,
  },
  saveIconStyle: {
    color: variables.bodyBg,
    fontSize: variables.fontSizeNormal,
    cursor: "pointer",
  },
  audioElementStyle: {
    display: variables.dFlex,
    height: variables.audioElementHeight,
  },
  audioPlayerWrapper: {
    display: "flex",
    alignItems: "center",
    width: variables.audioPlayContainerWidth,
    border: `1px solid  ${variables.grey4}`,
    background: variables.whiteSmoke,
    paddingRight: variables.paddingSmall2,
  },
  closeIcon: {
    height: variables.closeIcon,
    width: variables.closeIcon,
    cursor: "pointer",
  },
};
const AudioContainer = ({
  audioUploaded,
  setAudioUploaded,
  evidenceInProgress,
  setEvidenceInProgress,
  hideAction,
  questionId,
  question,
  onMediaChanged,
  fetchResponseId,
  responseId,
}) => {
  //NOSONAR
  const intl = useIntl();
  const audioRef = useRef(null);
  const showRecording = useRef(false);
  const loaderDispatch = useContext(GlobalLoaderContext)[1];
  const [remoteAuditState, remoteAuditDispatch] =
    useContext(RemoteAuditContext);
  const recorderControls = useAudioRecorder(
    {
      noiseSuppression: true,
      echoCancellation: true,
    },
    () => onAudioPermissionsNotFound() // onNotAllowedOrFound
  );
  const [mediaUrl, setMediaUrl] = useState(null);
  const [mediaId, setMediaId] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [evidenceNames, setEvidenceNames] = useState([]);

  const { auditId, id } = useParams();
  const isRemote = window.location.pathname.includes("remote");

  const onAudioPermissionsNotFound = () => {
    setOpenModal(true);
    showRecording.current = false;
  };

  const addAudioElement = (blob) => {
    const fileName = moment.now();
    const lastModified = new Date().getTime();
    const file = new File([blob], fileName, {
      type: blob.type,
      size: recorderControls.recordingTime,
      lastModified,
    });
    const formData = new FormData();
    formData.append("file", file);
    formData.append("fileName", fileName);
    formData.append("type", "AUDIO");
    MediaService.uploadMedia({
      loaderDispatch,
      body: formData,
      queryParams: { type: "AUDIO", auditId: auditId || id, questionId },
      intl,
    }).then((res) => {
      const newSnaps = audioUploaded?.length
        ? [...audioUploaded, res?.data?.data?.id]
        : [res?.data?.data?.id];
      fetchResponseId(res?.data?.data?.responseId);
      setAudioUploaded(newSnaps);
      setEvidenceInProgress(false);
      onMediaChanged(newSnaps, "audio");
    });
  };

  const deleteAudio = (audioId) => {
    MediaService?.deleteMediaData({
      loaderDispatch,
      mediaId: audioId,
      intl,
      params: {
        auditId: auditId || id,
        responseId: question?.questionResponse?.responseId || responseId,
      },
    }).then(() => {
      const updatedAudios = audioUploaded?.filter(
        (audioItem) => audioItem !== audioId
      );
      setAudioUploaded(updatedAudios);
      onMediaChanged(updatedAudios, "audio");
    });
  };

  const handleStartRecordingAudio = () => {
    if (!evidenceInProgress) {
      recorderControls.startRecording();
      setEvidenceInProgress(true);
      showRecording.current = true;
    } else {
      showNotification(
        "error",
        "Error",
        getLocalizedMessage(intl, "evidence.recordingAudioInProgressWarning")
      );
    }
  };

  const captureAudioRecordingRemoteVideoCall = () => {
    if (!remoteAuditState?.isEvidenceCompleted) {
      showNotification(
        "error",
        "Error",
        getLocalizedMessage(intl, "evidence.recordingAudioInProgressWarning")
      );
      return;
    }
    if (remoteAuditState?.dnsdSid && remoteAuditState?.mutedParticipent && remoteAuditState?.mutedParticipent[remoteAuditState?.dnsdSid]) {
      showNotification(
        "error",
        "Error",
        getLocalizedMessage(intl, "evidence.audioOff")
      );
      return;
    }
    const body = {
      auditId: parseInt(auditId),
      roomName: remoteAuditState?.room?.roomName,
      recordingType: "audio",
      recordingAction: 1,
      questionId: parseInt(questionId),
      fileName: `${moment.now()}`,
    };
    AuditService.recordEvidence({ intl, body }).then((res) => {
      remoteAuditDispatch(setIsEvidenceCompleted(false));
      remoteAuditDispatch(
        saveInprogressEvidenceInstance({
          documentId: parseInt(res?.data?.uploadId?.id),
          responseId: parseInt(res?.data?.responseId),
          type: "audio",
        })
      );
      fetchResponseId(res?.data?.responseId)
    });
  };
  const getMediaData = (audioEvidenceId) => {
    setMediaId(audioEvidenceId);
    setMediaUrl(null);
    MediaService?.getMediaData({
      loaderDispatch,
      mediaId: audioEvidenceId,
      intl,
    }).then((res) => {
      const mediaUrlFromBlob = URL.createObjectURL(res?.data);
      setMediaUrl(mediaUrlFromBlob);
      return () => URL.revokeObjectURL(mediaUrlFromBlob);
    });
  };

  const handleAudioPause = () => {
    setMediaUrl(null);
  };

  const getEvidenceName = async () => {
    // const res = await MediaService?.getMediaData({
    //   mediaId: mediaId,
    //   intl,
    // });
    // const fileName = getFileNameFromHeaders(
    //   res?.headers?.["content-disposition"]
    // );
    // return fileName;
  };

  useEffect(() => {
    if (audioUploaded?.length) {
      const fetchEvidenceNames = async () => {
        const names = await Promise.all(
          audioUploaded?.map((item) => getEvidenceName(item.documentId || item))
        );
        setEvidenceNames(names);
      };
      fetchEvidenceNames();
    }
  }, [audioUploaded]);

  const Icon = recorderControls?.isPaused ? CaretRightOutlined : PauseOutlined;

  return (
    <>
      <EvidenceContainer
        disabled={getConditionalContent({
          condition: isRemote,
          trueValue: !remoteAuditState?.dnsdSid,
        })}
        hideAction={hideAction}
        heading={getLocalizedMessage(intl, "evidence.recordings")}
        buttonLabel={getLocalizedMessage(intl, "evidence.recordAudio")}
        buttonIcon={"/icons/keyboard_voice1.svg"}
        onButtonClick={() =>
          isRemote
            ? captureAudioRecordingRemoteVideoCall()
            : handleStartRecordingAudio()
        }
        bottomSectionComponent={audioUploaded?.map((audioItem, index) => {
          return (
            <CardContainer
              key={audioItem}
              disabled={hideAction}
              onCrossClick={() => deleteAudio(audioItem)}
              title={`Audio ${getConditionalContent({
                condition:
                  evidenceNames[index] && evidenceNames[index] !== INVALID_DATE,
                trueValue: evidenceNames[index],
                falseValue: index + 1,
              })}`}
              icon={"/icons/keyboard_voice.svg"}
              onIconClick={() => {
                getMediaData(audioItem);
              }}
              showElement={
                mediaId === isRemote
                  ? audioItem?.documentId
                  : audioItem &&
                    mediaUrl && (
                      <div style={AudioContainerStyle.audioPlayerWrapper}>
                        <audio
                          onClick={(e) => e.preventDefault()}
                          id="audio-element"
                          controlsList="nodownload"
                          autoPlay
                          style={AudioContainerStyle.audioElementStyle}
                          ref={audioRef}
                          controls
                        >
                          <source src={mediaUrl} type="audio/webm" />
                        </audio>
                        <img
                          src={"/icons/close.svg"}
                          style={AudioContainerStyle.closeIcon}
                          alt="close"
                          onClick={() => {
                            handleAudioPause();
                          }}
                        />
                      </div>
                    )
              }
            />
          );
        })}
      />
      <CreatePortalCard open={true} id="root">
        <TwoRow
          style={{
            ...AudioContainerStyle.audioContainerStyle,
            ...{
              display: getConditionalContent({
                condition: showRecording?.current,
                trueValue: "flex",
                falseValue: "none",
              }),
            },
          }}
          topSection={
            <Text style={AudioContainerStyle.textStyle}>
              {getLocalizedMessage(intl, "evidence.recordingAudio")}
            </Text>
          }
          bottomSection={
            <TwoColumn
              style={AudioContainerStyle.columnMainStyle}
              leftSection={
                <AudioRecorder
                  onRecordingComplete={(blob) => {
                    addAudioElement(blob);
                  }}
                  showVisualizer
                  recorderControls={recorderControls}
                  onNotAllowedOrFound={onAudioPermissionsNotFound}
                />
              }
              rightSection={
                <div style={AudioContainerStyle.iconsWrapper}>
                  <Icon
                    style={AudioContainerStyle.playPauseIcon}
                    onClick={recorderControls.togglePauseResume}
                  />
                  <Button
                    style={AudioContainerStyle.btnStyle}
                    onClick={() => {
                      recorderControls.stopRecording();
                      showRecording.current = false;
                    }}
                    icon={
                      <SaveOutlined style={AudioContainerStyle.saveIconStyle} />
                    }
                  >
                    {getLocalizedMessage(intl, "button.save")}
                  </Button>
                </div>
              }
            />
          }
        />
      </CreatePortalCard>
      <ConfirmModalContainer
        hideButton
        question={getLocalizedMessage(intl, "provideMicrophoneAccess")}
        open={openModal}
        setOpen={setOpenModal}
        iconSrc={"/icons/warningIcon.svg"}
        onClose={() => {
          setOpenModal(false);
          showRecording.current = false;
          recorderControls.stopRecording();
          setEvidenceInProgress(false);
        }}
      />
    </>
  );
};

export default AudioContainer;
